import type { ProfileId, ProfilePattern } from "./materials.ts";

export type FixingAppearance = "exposed" | "concealed";
export type RidgeClosure = "soft_round" | "notched_square" | "standing_seam";
export type InstalledProfileFamily = "corrugated" | "trapezoidal" | "standing_seam" | "concealed_clip";
export type ProfileLod = "physical" | "shaded";

export interface InstalledFinishSpec {
  profileId: ProfileId;
  pattern: ProfilePattern;
  family: InstalledProfileFamily;
  moduleWidthM: number;
  ribHeightM: number;
  sheetCoverM: number | null;
  fixingAppearance: FixingAppearance;
  ridgeClosure: RidgeClosure;
  /** Representative eave fold used only to communicate how the sheet terminates. */
  eaveTurnDownM: number;
  profileClosure: "profiled_foam_or_metal" | "notched_metal" | "seamed_fold";
  sheen: "soft" | "balanced" | "architectural";
  /** Representative visible cover for the reference render, not a design specification. */
  ridgeCoverM: number;
  /** The Narwee reference uses the sourced 250 mm residential valley-gutter presentation. */
  valleyWidthM: number;
  roughness: number;
  metalness: number;
}

/** Compatibility name retained for the existing renderer/import surface. */
export type MetalFinishSpec = InstalledFinishSpec;

export interface CrossSectionPoint {
  offsetM: number;
  heightM: number;
}

const common = {
  eaveTurnDownM: 0.025,
  sheen: "balanced" as const,
};

export const METAL_FINISH_SPECS: Record<ProfileId, InstalledFinishSpec> = {
  corrugate: {
    profileId: "corrugate",
    pattern: "sinusoidal",
    family: "corrugated",
    moduleWidthM: 0.0762,
    ribHeightM: 0.019,
    sheetCoverM: 0.762,
    fixingAppearance: "exposed",
    ridgeClosure: "soft_round",
    ...common,
    profileClosure: "profiled_foam_or_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.56,
    metalness: 0.06,
  },
  true_oak_corrugate: {
    profileId: "true_oak_corrugate",
    pattern: "sinusoidal",
    family: "corrugated",
    moduleWidthM: 0.0762,
    ribHeightM: 0.0213,
    sheetCoverM: 0.762,
    fixingAppearance: "exposed",
    ridgeClosure: "soft_round",
    ...common,
    profileClosure: "profiled_foam_or_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.54,
    metalness: 0.06,
  },
  true_oak_deep: {
    profileId: "true_oak_deep",
    pattern: "sinusoidal",
    family: "corrugated",
    moduleWidthM: 0.1525,
    ribHeightM: 0.0403,
    sheetCoverM: 0.915,
    fixingAppearance: "exposed",
    ridgeClosure: "soft_round",
    ...common,
    profileClosure: "profiled_foam_or_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.52,
    metalness: 0.06,
  },
  trimrib: {
    profileId: "trimrib",
    pattern: "trapezoidal",
    family: "trapezoidal",
    moduleWidthM: 0.19,
    ribHeightM: 0.032,
    sheetCoverM: 0.76,
    fixingAppearance: "exposed",
    ridgeClosure: "notched_square",
    ...common,
    profileClosure: "notched_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.52,
    metalness: 0.06,
  },
  ribline_960: {
    profileId: "ribline_960",
    pattern: "trapezoidal",
    family: "trapezoidal",
    moduleWidthM: 0.16,
    ribHeightM: 0.032,
    sheetCoverM: 0.96,
    fixingAppearance: "exposed",
    ridgeClosure: "notched_square",
    ...common,
    profileClosure: "notched_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.51,
    metalness: 0.06,
  },
  multirib: {
    profileId: "multirib",
    pattern: "trapezoidal",
    family: "trapezoidal",
    moduleWidthM: 0.182,
    ribHeightM: 0.043,
    sheetCoverM: 0.91,
    fixingAppearance: "exposed",
    ridgeClosure: "notched_square",
    ...common,
    profileClosure: "notched_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.5,
    metalness: 0.06,
  },
  rt7: {
    profileId: "rt7",
    pattern: "trapezoidal",
    family: "trapezoidal",
    moduleWidthM: 0.127,
    ribHeightM: 0.043,
    sheetCoverM: 0.889,
    fixingAppearance: "exposed",
    ridgeClosure: "notched_square",
    ...common,
    profileClosure: "notched_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.5,
    metalness: 0.06,
  },
  eurostyle_spanlok: {
    profileId: "eurostyle_spanlok",
    pattern: "standing_seam",
    family: "standing_seam",
    moduleWidthM: 0.45,
    ribHeightM: 0.045,
    sheetCoverM: null,
    fixingAppearance: "concealed",
    ridgeClosure: "standing_seam",
    eaveTurnDownM: 0.03,
    profileClosure: "seamed_fold",
    sheen: "architectural",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.47,
    metalness: 0.06,
  },
  eurostyle_epic: {
    profileId: "eurostyle_epic",
    pattern: "standing_seam",
    family: "standing_seam",
    moduleWidthM: 0.45,
    ribHeightM: 0.038,
    sheetCoverM: null,
    fixingAppearance: "concealed",
    ridgeClosure: "standing_seam",
    eaveTurnDownM: 0.03,
    profileClosure: "seamed_fold",
    sheen: "architectural",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.46,
    metalness: 0.06,
  },
  eurostyle_eurolok: {
    profileId: "eurostyle_eurolok",
    pattern: "standing_seam",
    family: "standing_seam",
    moduleWidthM: 0.45,
    ribHeightM: 0.05,
    sheetCoverM: null,
    fixingAppearance: "concealed",
    ridgeClosure: "standing_seam",
    eaveTurnDownM: 0.03,
    profileClosure: "seamed_fold",
    sheen: "architectural",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.46,
    metalness: 0.06,
  },
  multidek: {
    profileId: "multidek",
    pattern: "trapezoidal",
    family: "concealed_clip",
    moduleWidthM: 0.5,
    ribHeightM: 0.05,
    sheetCoverM: 0.5,
    fixingAppearance: "concealed",
    ridgeClosure: "notched_square",
    eaveTurnDownM: 0.03,
    profileClosure: "notched_metal",
    sheen: "architectural",
    ridgeCoverM: 0.15,
    valleyWidthM: 0.25,
    roughness: 0.48,
    metalness: 0.06,
  },
  ri925: {
    profileId: "ri925",
    pattern: "trapezoidal",
    family: "trapezoidal",
    moduleWidthM: 0.185,
    ribHeightM: 0.041,
    sheetCoverM: 0.925,
    fixingAppearance: "exposed",
    ridgeClosure: "notched_square",
    ...common,
    profileClosure: "notched_metal",
    ridgeCoverM: 0.13,
    valleyWidthM: 0.25,
    roughness: 0.5,
    metalness: 0.06,
  },
};

export function metalFinishForProfile(profileId: ProfileId): InstalledFinishSpec {
  return METAL_FINISH_SPECS[profileId];
}

export function profileCrossSection(spec: InstalledFinishSpec): CrossSectionPoint[] {
  const half = spec.moduleWidthM / 2;
  if (spec.pattern === "sinusoidal") {
    const segments = 10;
    return Array.from({ length: segments + 1 }, (_, index) => {
      const ratio = index / segments;
      const offsetM = -half + spec.moduleWidthM * ratio;
      const heightM = spec.ribHeightM * (0.5 + 0.5 * Math.cos(ratio * Math.PI * 2 - Math.PI));
      return { offsetM, heightM };
    });
  }
  if (spec.pattern === "trapezoidal") {
    return [
      { offsetM: -half, heightM: 0 },
      { offsetM: -spec.moduleWidthM * 0.22, heightM: 0 },
      { offsetM: -spec.moduleWidthM * 0.11, heightM: spec.ribHeightM },
      { offsetM: spec.moduleWidthM * 0.11, heightM: spec.ribHeightM },
      { offsetM: spec.moduleWidthM * 0.22, heightM: 0 },
      { offsetM: half, heightM: 0 },
    ];
  }
  const seamHalf = 0.018;
  const shoulder = 0.03;
  return [
    { offsetM: -half, heightM: 0 },
    { offsetM: -shoulder, heightM: 0 },
    { offsetM: -seamHalf, heightM: spec.ribHeightM * 0.9 },
    { offsetM: -seamHalf * 0.72, heightM: spec.ribHeightM },
    { offsetM: seamHalf * 0.72, heightM: spec.ribHeightM },
    { offsetM: seamHalf, heightM: spec.ribHeightM * 0.9 },
    { offsetM: shoulder, heightM: 0 },
    { offsetM: half, heightM: 0 },
  ];
}

export interface SheetSetOut {
  alongEave: { x: number; y: number };
  fallDirection: { x: number; y: number };
}

/** Derives sheet direction exclusively from the supplied eave edge and plane ring. */
export function sheetSetOut(
  ring: Array<[number, number]>,
  eaveEdge: [number, number],
): SheetSetOut | null {
  const a = ring[eaveEdge[0]];
  const b = ring[eaveEdge[1]];
  if (!a || !b) return null;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length <= 1e-8) return null;
  const alongEave = { x: dx / length, y: dy / length };
  const normal = { x: -alongEave.y, y: alongEave.x };
  const centroid = ring.reduce((sum, point) => ({ x: sum.x + point[0], y: sum.y + point[1] }), { x: 0, y: 0 });
  centroid.x /= ring.length;
  centroid.y /= ring.length;
  const inward = (centroid.x - a[0]) * normal.x + (centroid.y - a[1]) * normal.y >= 0 ? 1 : -1;
  return { alongEave, fallDirection: { x: -normal.x * inward, y: -normal.y * inward } };
}

export function projectedModulePixels(
  moduleWidthM: number,
  cameraDistanceM: number,
  viewportHeightPx: number,
  verticalFovDeg: number,
): number {
  if (![moduleWidthM, cameraDistanceM, viewportHeightPx, verticalFovDeg].every(Number.isFinite)) return 0;
  if (moduleWidthM <= 0 || cameraDistanceM <= 0 || viewportHeightPx <= 0 || verticalFovDeg <= 0 || verticalFovDeg >= 180) return 0;
  const worldHeight = 2 * cameraDistanceM * Math.tan((verticalFovDeg * Math.PI) / 360);
  return (moduleWidthM / worldHeight) * viewportHeightPx;
}

/** Physical ribs below this projected density alias into Moire, so shaded detail takes over. */
export function profileLodFromProjectedPixels(pixelsPerModule: number): ProfileLod {
  return pixelsPerModule >= 5.5 ? "physical" : "shaded";
}

export function alignedModuleCentres(minU: number, maxU: number, moduleWidthM: number): number[] {
  if (![minU, maxU, moduleWidthM].every(Number.isFinite) || moduleWidthM <= 0 || maxU < minU) return [];
  const startIndex = Math.floor(minU / moduleWidthM) - 1;
  const endIndex = Math.ceil(maxU / moduleWidthM) + 1;
  return Array.from({ length: endIndex - startIndex + 1 }, (_, index) => (startIndex + index) * moduleWidthM);
}

export function alignedSheetJoints(minU: number, maxU: number, sheetCoverM: number | null): number[] {
  if (sheetCoverM === null) return [];
  return alignedModuleCentres(minU, maxU, sheetCoverM).filter((offset) => offset > minU && offset < maxU);
}
